import { TranslationItem, ITranslationItem } from '../../globals/classes/translationItem';
import { StringUtils } from '../../globals/classes/utilities';
import { IPreferredUserExperience } from './http-requests';

export interface IImpersonationUserStatusResponse {
	userType: string;
	userStatus: string;
	ux: IPreferredUserExperience [];
	additionalAuthenticationInfo: ITwoFactorAuthenticationInfo;
}

export interface IFaqSectionsResponse {
	faqSections: IFaqSection[];
}
export interface IFaqSection {
	title: string;
	userType: string;
	sortOrder: number;
	faqs: IFaq[];
	icon?: string;
	active?: boolean;
}
export interface IFaq {
	question: string;
	answer: string;
	sortOrder: number;
}
export interface IGetLoginResponse {
    allowRememberLogin: boolean;
    enableLocalLogin: boolean;
    externalLoginScheme: string;
    isExternalLoginOnly: boolean;
    returnUrl: string;
    username: string;
	clientUX: string;
    clientId: string;
}
export interface ILoginResponse {
	additionalAuthenticationInfo: ITwoFactorAuthenticationInfo;
}
export interface ITwoFactorAuthenticationInfo {
	actualUserId: string;
	reasonInfo: ITwoFactorAuthenticationReason;
	verificationCodeInfo: IVerificationCodeInfo;
}

export interface ITwoFactorAuthenticationReason {
	reasonDescription: string;
	reasonCode: string;
}

export interface IVerificationCodeInfo {
	codeLifetimeInSeconds: number;
	timestamp: Date;
	target: string;
	hasOtp: boolean;
}

export interface IForgotEmailResponse {
	matches: string[];
}

export interface ITranslationsResponse {
	translations: ITranslationItem[];
}

export interface ISendSmsOtpResponse {
	hasOtp: boolean;
	succeeded: boolean;
	maskedPhoneNumber?: string;
}

export interface IVerifyCodeResponse {
	succeeded: boolean;
	errorMsg: string;
}

export interface ILinkTargetResponse {
	userId: string;
	company: string;
	hasOtp: boolean;
}

export interface ILinkUsersResponse {
	userId: string;
	company: string;
	autoRedirectTimeout: number;
	expiration: string;
}

export interface IInitiateLinkProcessResponse {
	userType: string;
	userId: string;
	company: string;
	hasOtp: boolean;
}

export class FaqSectionsResponse implements IFaqSectionsResponse {
	faqSections: FaqSection[];

	constructor(resp?: IFaqSectionsResponse) {
		let newResp: IFaqSectionsResponse = resp || {
			faqSections: [],
		};

		this.read(newResp);
	}

	private read(newResp: IFaqSectionsResponse) {
		this.faqSections = [];
		if (newResp.faqSections) {
			newResp.faqSections.forEach((t) => this.faqSections.push(new FaqSection(t)));
		}
	}
}

export class FaqSection implements IFaqSection {
	title: string;
	userType: string;
	sortOrder: number;
	faqs: Faq[];
	icon?: string;
	active?: boolean;

	constructor(item?: IFaqSection) {
		let newItem: IFaqSection = item || {
			title: undefined,
			sortOrder: undefined,
			userType: undefined,
			faqs: [],
			icon: undefined,
			active: undefined,
		};

		this.read(newItem);
	}
	public search(term: string): Faq[] {
		return this.faqs
			.filter((faq) => this._matchSearchTerm(term, faq.question))
			.sort((faqA, faqB) => (faqA.sortOrder > faqB.sortOrder ? 1 : -1));
	}
	private _matchSearchTerm(term: string, value: string) {
		if (!term) return true;
		if (!value) return false;
		return StringUtils.formatSearch(value).indexOf(StringUtils.formatSearch(term)) >= 0;
	}
	public isRetail(): boolean {
		return this.userType == 'RETAIL' || this.userType == 'ALL';
	}

	public isNonRetail(): boolean {
		return this.userType == 'ALL' || this.userType != 'RETAIL';
	}

	private read(newItem: IFaqSection) {
		this.title = newItem.title;
		this.sortOrder = newItem.sortOrder;
		this.userType = newItem.userType;
		this.faqs = [];
		if (newItem.faqs) {
			newItem.faqs.forEach((t) => this.faqs.push(new Faq(t)));
		}
	}
}

export class Faq implements IFaq {
	answer: string;
	sortOrder: number;
	question: string;

	constructor(item?: IFaq) {
		let newItem: IFaq = item || {
			question: undefined,
			sortOrder: undefined,
			answer: undefined,
		};

		this.read(newItem);
	}
	private read(newItem: IFaq) {
		this.question = newItem.question;
		this.sortOrder = newItem.sortOrder;
		this.answer = newItem.answer;
	}
}

export class TwoFactorAuthenticationInfo implements ITwoFactorAuthenticationInfo {
	actualUserId: string;
	reasonInfo: ITwoFactorAuthenticationReason;
	verificationCodeInfo: VerificationCodeInfo;

	constructor(resp?: ITwoFactorAuthenticationInfo) {
		let newResp: ITwoFactorAuthenticationInfo = resp || {
			actualUserId: undefined,
			reasonInfo: undefined,
			verificationCodeInfo: undefined,
		};

		this.read(newResp);
	}

	private read(newResp: ITwoFactorAuthenticationInfo) {
		this.actualUserId = newResp.actualUserId;
		this.reasonInfo = newResp.reasonInfo;
		this.verificationCodeInfo = !!newResp.verificationCodeInfo
			? new VerificationCodeInfo(newResp.verificationCodeInfo)
			: null;
	}
}

export class VerificationCodeInfo implements IVerificationCodeInfo {
	codeLifetimeInSeconds: number;
	timestamp: Date;
	target: string;
	hasOtp: boolean;

	constructor(resp?: IVerificationCodeInfo) {
		let newResp: IVerificationCodeInfo = resp || {
			codeLifetimeInSeconds: undefined,
			timestamp: undefined,
			target: undefined,
			hasOtp: undefined,
		};

		this.read(newResp);
	}

	private read(newResp: IVerificationCodeInfo) {
		this.codeLifetimeInSeconds = newResp.codeLifetimeInSeconds;
		this.target = newResp.target;
		this.timestamp = !!newResp.timestamp ? new Date(newResp.timestamp) : null;
		this.hasOtp = newResp.hasOtp;
	}
}

export class TranslationsResponse implements ITranslationsResponse {
	translations: TranslationItem[];

	constructor(resp?: ITranslationsResponse) {
		let newResp: ITranslationsResponse = resp || {
			translations: [],
		};

		this.read(newResp);
	}

	private read(newResp: ITranslationsResponse) {
		this.translations = [];
		if (newResp.translations) {
			newResp.translations.forEach((t) => this.translations.push(new TranslationItem(t)));
		}
	}
}

export class ForgotEmailResponse implements IForgotEmailResponse {
	matches: string[];

	constructor(resp?: IForgotEmailResponse) {
		let newResp: IForgotEmailResponse = resp || {
			matches: [],
		};

		this.read(newResp);
	}

	private read(newResp: IForgotEmailResponse) {
		this.matches = [];
		if (newResp.matches) {
			newResp.matches.forEach((m) => this.matches.push(m));
		}
	}
}
